import { CONTRACT_SIGN_FORMS, type ContractSignForm, type OfferDto } from '@bqcrm/shared';
import { Button, Card, Group, NumberInput, Select, Stack, Text, Textarea } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../../api';
import { notifyError, notifyOk } from '../../notify';

/**
 * Ustalenia do umowy (QA P33): zaliczka %, forma podpisu, uwagi dla działu umów. Na poziomie oferty — można je
 * zmieniać także po wydaniu i akceptacji (nie trafiają do treści dla klienta). Umowa bierze z nich domyślny harmonogram.
 */
export function ContractTermsCard({ o }: { o: OfferDto }) {
  const qc = useQueryClient();
  const init = () => ({ depositPct: o.contractTerms?.depositPct ?? null, signForm: o.contractTerms?.signForm ?? null, note: o.contractTerms?.note ?? '' });
  const [form, setForm] = useState(init);
  useEffect(() => setForm(init()), [o.contractTerms]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = JSON.stringify(form) !== JSON.stringify(init());
  const save = useMutation({
    mutationFn: () => api<OfferDto>(`/offers/${o.id}/contract-terms`, { method: 'PUT', body: { ...form, note: form.note.trim() } }),
    onSuccess: (r) => {
      qc.setQueryData(['offer', o.id], r);
      notifyOk('Zapisano ustalenia do umowy');
    },
    onError: notifyError,
  });
  const ro = !o.can.edit;
  if (ro && !o.contractTerms) return null;
  return (
    <Card withBorder>
      <Stack gap="xs">
        <Text fw={600} size="sm">
          Ustalenia do umowy
        </Text>
        <Text size="xs" c="dimmed">
          Wewnętrzne — nie trafiają do oferty dla klienta. Dział umów dostanie je przy przygotowaniu umowy (zaliczka ustawi domyślny harmonogram).
        </Text>
        <NumberInput
          label="Zaliczka"
          suffix="%"
          placeholder="jak w ustawieniach firmy"
          min={0}
          max={100}
          allowDecimal={false}
          value={form.depositPct ?? ''}
          disabled={ro}
          onChange={(v) => setForm({ ...form, depositPct: v === '' || v === null ? null : Number(v) })}
        />
        <Select
          label="Forma podpisu"
          placeholder="bez ustaleń"
          clearable
          data={Object.entries(CONTRACT_SIGN_FORMS).map(([value, label]) => ({ value, label }))}
          value={form.signForm}
          disabled={ro}
          onChange={(v) => setForm({ ...form, signForm: (v as ContractSignForm | null) ?? null })}
        />
        <Textarea label="Uwagi dla działu umów" autosize minRows={2} maxLength={1000} value={form.note} disabled={ro} onChange={(e) => setForm({ ...form, note: e.currentTarget.value })} />
        {!ro && (
          <Group justify="flex-end">
            <Button size="xs" variant="light" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>
              Zapisz ustalenia
            </Button>
          </Group>
        )}
      </Stack>
    </Card>
  );
}
